import { Howler, Howl } from 'howler'
import bowser from 'bowser'

let sounds = {}
let locked = []
let allMuted = false
let SFXMuted = false
let musicMuted = false

function initialize(_sounds, updateProgress) {
  sounds = {}

  updateProgress('sounds', 0)

  document.addEventListener('visibilitychange', () => {
    Howler.mute(document.hidden ? true : allMuted)
  })

  // Additional mute check for specific mobile devices.
  /*if (bowser.mobile){
    window.addEventListener('blur', () => {
      Howler.mute(true)
    })

    window.addEventListener('focus', () => {
      Howler.mute(allMuted)
    })
  }*/ // Disabled due to issues on Betfair

  let loaded = 0

  return new Promise(resolve => {
    let toLoad = 0

    _sounds.forEach(async sound => {
      toLoad++
      let data
      let json
      let howl

      if (sound.format === 'map') {
        data = await fetch(sound.src)
        json = await data.json()
        json.src = json.urls

        howl = new Howl(json)

      } else {

        howl = new Howl({
          ...sound
        })
        sounds[sound.id] = howl
      }

      howl.once('load', () => {
        howl.off('loaderror')
        loaded++
        updateProgress('sounds', (loaded / toLoad) * 100)
        if (loaded === toLoad) {
          resolve()
        }

        Object.keys(howl._sprite).forEach(key => {
          const spriteData = json.sprite[key]
          sounds[key] = new Howl({
            src: json.src,
            sprite: { [key]: [ spriteData[0], spriteData[1] ] }
          })

          const isMusic = key.indexOf('music_') > -1
          if ((SFXMuted && !isMusic) || (musicMuted && isMusic)) {
            sounds[key].mute(true)
          }
        })
        webAudioCtxResume()
      })

      howl.once('loaderror', () => {
        console.warn(`Failed to load: ${sound.id} \n\n src: ${sound.src}`)
      })
    })
  })
}

/**
 * Attempts to resume the webAudioApi
 * context created by howler.
 * The context can be suspended by browsers
 * due to autoplay policies.
 */
function webAudioCtxResume() {
  Howler.ctx.resume()
}

/**
 * Replaces the current sounds with new sounds.
 * @param {object} replacements - An object with the sound keys and its properties.
 */
function replaceSounds(replacements) {
  Object.keys(replacements).forEach(s => {
    if (replacements[s].src !== null) {
      sounds[replacements[s].id] = { src: replacements[s].src, type: replacements[s].type }
    }
  })
}

/**
 * Replaced a single sound with a new sound.
 * @param {string} key - The name of the sound to be replaced.
 * @param {string} source - The source of the new sound file.
 * @param {number} volume - Volume of the new sound.
 */
function replaceSound(key, source, volume) {
  sounds[key] = new Howl({
    id: key,
    src: source,
    type: 'file',
    volume: volume || 1
  })
}

/**
 * Attempt to play a sound.
 * @param {string} soundName - Name of the sound to be played.
 * @param {number} pitch - Pitch value of the sound.
 * @param {number} volume - Volume of the sound.
 * @returns {*}
 */
function playSound(soundName, pitch = 1, volume) {
  if (!sounds[soundName]) {
    console.warn(`Sound: "${soundName}" missing or not yet loaded`)
    return
  }

  let ret

  if (pitch !== 1 || volume) {
    let modSound = sounds[soundName].play(soundName)
    if (modSound === null) {
      modSound = sounds[soundName].play()
    }
    sounds[soundName].rate(pitch, modSound)
    sounds[soundName].volume(volume, modSound)
    ret = modSound
  } else {
    ret = sounds[soundName].play(soundName)
    if (ret === null) {
      ret = sounds[soundName].play()
    }
  }

  return ret
}

/**
 * Play a sound and set it to loop.
 * @param {string} soundName - Name of the sound.
 * @param {boolean} bool - Whether the sound should loop or not.
 * @param {boolean} silent - Whether the sound should start with volume 0.
 */
function loopSound(soundName, bool = true, silent) {
  if (!sounds[soundName]) {
    console.warn(`Sound: "${soundName}" missing or not yet loaded`)
    return
  }

  if (silent) {
    setVolume(soundName, 0)
  }
  sounds[soundName].loop(bool)
  return playSound(soundName)
}

/**
 * Plays and locks a sound, preventing
 * further plays until the lock is released
 * @param {string} soundName - Name of the sound.
 * @param {boolean} release - lock released if true
 */
function soundLock(soundName, release) {
  if (!sounds[soundName]) {
    console.warn(`Sound: "${soundName}" missing or not yet loaded`)
    return
  }

  if (release) {
    const toBeReleased = locked.indexOf(soundName)
    if (toBeReleased > -1) {
      locked.splice(toBeReleased, 1)
      return
    }
  }
  if (locked.indexOf(soundName) > -1) {
    // sound is locked
    return
  }

  playSound(soundName)
  locked.push(soundName)
}

/**
 * Releases all locked sounds
 */
function lockResetAll() {
  locked = []
}

/**
 * Returns all locked sounds
 * @returns {string[]}
 */
function getLocked() {
  return locked
}

/**
 * Checks if the sound is currently playing.
 * @param {string} soundName - Name of the sound.
 * @returns {boolean} - If the sound is playing or not.
 */
function isPlaying(soundName) {
  if (!sounds[soundName]) {
    console.warn(`Sound: "${soundName}" missing or not yet loaded`)
    return false
  }

  return sounds[soundName].playing()
}

/**
 * Stops the sound from playing.
 * @param {string} soundName - The name of the sound.
 * @param {number} id - The number id of the sound, received as return value
 * when playing a sound.
 */
function stopSound(soundName, id) {
  if (!sounds[soundName]) {
    console.warn(`Sound: "${soundName}" missing or not yet loaded`)
    return
  }

  sounds[soundName].stop(id)
}

/**
 * Pauses the sound.
 * @param {string} soundName - he name of the sound.
 */
function pauseSound(soundName) {
  if (!sounds[soundName]) {
    console.warn(`Sound: "${soundName}" missing or not yet loaded`)
    return
  }

  sounds[soundName].pause()
}

/**
 * Fades out the sound gradually.
 * @param {string} soundName - The name of the sound.
 * @param {number} duration - The duration of the fadeout (in milliseconds).
 * @param {number} from - The starting volume the fadeout should start from.
 */
function fadeOut(soundName, duration = 300, from) {
  if (from) {
    fadeSound(soundName, 0, duration, from)
  } else {
    const currentVol = getVolume(soundName)
    fadeSound(soundName, 0, duration, currentVol)
  }
}

/**
 * Fades in the sound gradually.
 * @param {string} soundName - The name of the sound.
 * @param {number} duration - The duration of the fadeout (in milliseconds).
 * @param {number} to - The ending volume which the fadeout should end at.
 */
function fadeIn(soundName, duration = 300, to) {
  const currentVol = getVolume(soundName)
  if (currentVol > 0) {
    fadeSound(soundName, to, duration, currentVol)
  } else {
    fadeSound(soundName, to, duration, 0)
  }
}

/**
 * The main fade function to fade sound from one volume to another.
 * @param {string} soundName - The name of the sound.
 * @param {number} to - The goal volume of the fade.
 * @param {number} duration - The duration of the fade (in milliseconds).
 * @param {*} forceVolume - Force the starting volume of the sound.
 */
function fadeSound(soundName, to = 0, duration = 300, forceVolume = null) {
  if (!sounds[soundName]) {
    console.warn(`Sound: "${soundName}" missing or not yet loaded`)
    return
  }

  if (forceVolume !== null) {
    if (forceVolume === 0 && to === 0) {
      return
    }
    sounds[soundName].fade(forceVolume, to, duration)
  } else {
    const currentVol = sounds[soundName].volume()
    sounds[soundName].fade(currentVol, to, duration)
  }
}

/**
 * Returns all the sounds available.
 * @returns {{}}
 */
function getSoundList() {
  let ret = { ...sounds }
  return ret
}

/**
 * Get a single sound.
 * @param {string} soundName - The name of the sound.
 * @returns {*} - Returns a reference to the howl.
 */
function getSound(soundName) {
  if (sounds[soundName]) {
    return sounds[soundName]
  }
  console.warn(`Sound ${soundName} not found.`)
  return undefined
}

/**
 * Get all the sound files which are not music sounds.
 * @returns {string[]}
 */
function getSFXList() {
  return Object.keys(sounds).filter(sound => !sound.includes('music'))
}

/**
 * Get all the sounds which have 'music' in their name.
 * @returns {string[]}
 */
function getMusicList() {
  return Object.keys(sounds).filter(sound => sound.includes('music'))
}

/**
 * Get all multi track music sounds.
 * @returns {string[]}
 */
function getMultiTrackList() {
  return Object.keys(sounds).filter(sound => sound.includes('music_multi'))
}

/**
 * Mutes the Howler instance globally.
 * @param {boolean} mute - If the sounds should be muted or not.
 */
function muteAll(mute) {
  allMuted = mute
  Howler.mute(mute)
}

/**
 * Mutes all the SFX sounds, not the music.
 * @param {boolean} mute - If the SFX should be muted or not.
 */
function muteSFX(mute) {
  SFXMuted = mute
  const allSFX = getSFXList()
  allSFX.forEach(soundName => {
    sounds[soundName].mute(mute)
  })
}

/**
 * Mutes all the Music sounds, not the SFX.
 * @param {boolean} mute -If the Music should be muted or not.
 */
function muteMusic(mute) {
  musicMuted = mute
  const allMusic = getMusicList()
  allMusic.forEach(soundName => {
    sounds[soundName].mute(mute)
  })
}

/**
 * Stop all the current sounds from playing.
 */
function stopAll() {
  Object.keys(sounds).forEach(key => {
    if (sounds[key])
      sounds[key].stop()
  })
}

/**
 * Set the volume of a sound.
 * @param {string} soundName - The name of the sound.
 * @param {number} volume - The new volume.
 */
function setVolume(soundName, volume) {
  if (!sounds[soundName]) {
    console.warn(`Sound: "${soundName}" missing or not yet loaded`)
    return
  }
  sounds[soundName].volume(volume)
}

function getVolume(soundName) {
  return sounds[soundName] ? sounds[soundName].volume(soundName) : 0
}

module.exports = {
  initialize,
  webAudioCtxResume,
  replaceSounds,
  replaceSound,
  playSound,
  loopSound,
  pauseSound,
  stopSound,
  fadeSound,
  fadeOut,
  fadeIn,
  soundLock,
  getLocked,
  lockResetAll,
  isPlaying,
  getSoundList,
  getMusicList,
  getMultiTrackList,
  getSound,
  setVolume,
  getVolume,
  muteMusic,
  muteSFX,
  muteAll,
  stopAll
}
